/**
 * Shared Fee Output line-item builder.
 *
 * The Fee Output block is rendered on three surfaces that must always agree:
 *   1. the output screen  - components/feeOutput.js/OutputRightSide.js
 *   2. the online PDF     - components/utility/generateFeesReport.js
 *   3. the bot summary PDF - DMV-bot-refi-fees/generate_fees_html.py
 *
 * Surfaces 1 and 2 previously carried byte-identical copies of the flatten and
 * split logic, which is how they drifted from the bot in the first place. Both
 * now call through here so there is a single definition to change.
 *
 * DMV-574 originally specified the order ATC returns in <FeeDetails>, preserved
 * by clean_response writing every line item into one flat dict. That is not
 * reproducible on the bot: the bot reads its fees out of a DynamoDB map, which
 * has no meaningful ordering, so <FeeDetails> order is lost before the bot ever
 * sees the data. Ordering alphabetically by label is deterministic from the data
 * alone, so all three surfaces can arrive at it independently. If the ordering
 * changes again, it has to change here AND in the Python helper
 * (build_fee_items in generate_fees_html.py), which mirrors these semantics.
 */

// Response keys that are metadata rather than displayable line items.
const EXCLUDED_KEYS = [
  "Inputs",
  "Notes",
  "Type",
  "TransactionID",
  "HTML Data",
  "_FeeOrder",
];

const isFeeGroup = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

// Case- and whitespace-insensitive label comparison, so " title fee" and
// "Title Fee" sort together. localeCompare with the numeric option keeps
// labels like "Other Fee 2" ahead of "Other Fee 10".
export const compareFeeLabels = (a, b) =>
  String(a).trim().toLowerCase()
    .localeCompare(String(b).trim().toLowerCase(), undefined, { numeric: true });

/**
 * Flatten a fees response into a sorted, alphabetised list of
 * [label, amount] pairs.
 *
 * Nested groups (the bot-side response nests line items under "Fees"/"Taxes")
 * are expanded in place so every returned amount gets its own row instead of
 * rendering as "[object Object]".
 */
export const buildFeeItems = (response) =>
  Object.entries(response || {})
    .filter(([key]) => !EXCLUDED_KEYS.includes(key))
    .flatMap(([key, value]) =>
      isFeeGroup(value) ? Object.entries(value) : [[key, value]]
    )
    .sort(([labelA], [labelB]) => compareFeeLabels(labelA, labelB));

/**
 * Split line items into two balanced columns. The left column takes the extra
 * row when the count is odd, so reading down the left column and continuing
 * down the right yields the full alphabetical sequence.
 */
export const splitFeeColumns = (items) => {
  const half = Math.ceil(items.length / 2);
  return [items.slice(0, half), items.slice(half)];
};
